import {
  Color,
  Euler,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Quaternion,
  Vector3,
  Web3DViewer,
} from '@technology/web3d';
import {
  getMatrixColumnMagnitude,
  MatrixColumn,
  scaleMatrixColumn,
  ThreeJsManager,
} from '@trimble/web3d-utils';
import { StationInfo } from '@trimble/web3d-providers';
import { calculateDistanceBasedScale } from '../distance-based-scale-calculation';
import { MAP_STATION_PATH_COLOR } from '../../capabilities/constants/map-station';
import {
  createPathSegmentsVisuals,
  type StationData,
  type PathSegment,
  type StationVisualConfig,
} from './station-visual-creator';
import { createSinglePathSegmentMesh } from './station-path-mesh';
import { PathClipApplier } from './path-clip-applier';
import {
  createInstancedPathMesh,
  updateInstanceColor,
  type PathInstanceData,
} from './instanced-station-path-mesh';

const PATH_HEIGHT_OFFSET = 0.05;
/** Hidden paths stay in the scene; this keeps them out of the raycaster. */
const ignorePathRaycast = (): void => undefined;
const PATH_RENDER_ORDER = 1;
const PATH_OPACITY_MIN = 0.3;
const PATH_OPACITY_MAX = 0.7;
const OPACITY_ANIMATION_SPEED = 0.08;
const PATH_WIDTH_CHANGE_THRESHOLD = 0.01;
const SCALE_ZERO_THRESHOLD = 0.001;
const SCALE_ANIMATION_DISTANCE_THRESHOLD = 1.5;
const COLOR_ANIMATION_SPEED = 0.05;
const ANIMATION_TOLERANCE = 0.01;
const LOD_FAR_PATH_COLOR = new Color(1, 1, 1);
// Far segments need extra width so the path stays visible when zoomed out.
const FAR_PATH_WIDTH_MULTIPLIER = 15;

export interface StationPathPipelineConfig {
  readonly stationPlaneSize: number;
  readonly stationPointSize: number;
  readonly pathWidthRatio: number;
  readonly stationNearBoundary: number;
  readonly stationFarBoundary: number;
  readonly useInstancedPaths: boolean;
}

export interface StationPathPipelineDeps {
  getStationDataByIndex(index: number): StationData | undefined;
  /** Overridden by 2D map for a fixed Z. */
  getStationZ(position: Vector3): number;
  isStationMarkersVisible(): boolean;
  isNavigationPathVisible(): boolean;
  /**
   * Read once at create time to seed the initial opacity baseline; ongoing
   * changes flow through {@link StationPathPipeline.setTargetOpacity}.
   */
  getCurrentStationPosition(): Vector3 | null;
}

/**
 * @intent Owns every path-mesh resource and the animations that touch
 * them. Three rendering modes coexist:
 *   1. **Non-instanced segments** — one mesh per segment; legacy path.
 *   2. **Instanced far path** — a single `InstancedMesh` for all segments,
 *      drawn white when stations are too far for marker rendering.
 *   3. **Hybrid near segments** — once an instanced segment's endpoints
 *      become LOD-active, an individual mesh replaces it (jitter-free
 *      near rendering) and the instanced slot is zero-scaled out.
 *
 * Clip-volume visibility is delegated to {@link PathClipApplier}.
 */
export class StationPathPipeline {
  private readonly viewer: Web3DViewer;
  private readonly threeJsManager: ThreeJsManager;
  private readonly pathClipApplier: PathClipApplier;
  private readonly config: StationPathPipelineConfig;
  private readonly visualConfig: StationVisualConfig;
  private readonly deps: StationPathPipelineDeps;

  protected pathSegments: Array<PathSegment> = [];

  private farPathMesh: InstancedMesh | null = null;
  private farPathData: PathInstanceData[] = [];
  private farPathTargetColors: Color[] = [];
  private farPathCurrentColors: Color[] = [];
  /** Segment-index → individual near-mesh (created lazily on LOD entry). */
  private readonly nearPathMeshes: Map<number, Mesh> = new Map();
  /** Reverse lookup for hover/pick. */
  private readonly nearPathMeshIdToSegmentIndex: Map<number, number> =
    new Map();

  protected targetPathOpacity: number = PATH_OPACITY_MAX;
  private currentPathOpacity = PATH_OPACITY_MAX;
  private pathOpacityAnimating = false;
  private pathColorsAnimating = false;

  private readonly tempMatrix: Matrix4 = new Matrix4();
  private static readonly ZERO_SCALE = new Vector3(0, 0, 0);
  /**
   * Raycast captured before a hide override. Restoring this reference keeps
   * a later instance override, instead of deleting `raycast` and falling
   * through to the prototype.
   */
  private readonly pathMeshRaycasts = new WeakMap<Mesh, Mesh['raycast']>();

  constructor(
    viewer: Web3DViewer,
    threeJsManager: ThreeJsManager,
    pathClipApplier: PathClipApplier,
    config: StationPathPipelineConfig,
    visualConfig: StationVisualConfig,
    deps: StationPathPipelineDeps
  ) {
    this.viewer = viewer;
    this.threeJsManager = threeJsManager;
    this.pathClipApplier = pathClipApplier;
    this.config = config;
    this.visualConfig = visualConfig;
    this.deps = deps;
  }

  public get segments(): readonly PathSegment[] {
    return this.pathSegments;
  }

  public get instancedFarPathMesh(): InstancedMesh | null {
    return this.farPathMesh;
  }

  public get instancedFarPathData(): readonly PathInstanceData[] {
    return this.farPathData;
  }

  public get hasActiveColorAnimation(): boolean {
    return this.pathColorsAnimating;
  }

  public get hasActiveOpacityAnimation(): boolean {
    return this.pathOpacityAnimating;
  }

  /** Prevents stale flags from keeping the manager ticking after tear-down. */
  public resetAnimationFlags(): void {
    this.pathOpacityAnimating = false;
    this.pathColorsAnimating = false;
  }

  /** Seeds opacity from the selection state so the initial render matches. */
  public createPaths(stationsInfo: StationInfo[]): void {
    const pathOpacity = this.deps.getCurrentStationPosition()
      ? PATH_OPACITY_MIN
      : PATH_OPACITY_MAX;

    this.currentPathOpacity = pathOpacity;
    this.targetPathOpacity = pathOpacity;

    if (this.config.useInstancedPaths) {
      this.createInstancedPaths(stationsInfo, pathOpacity);
    } else {
      this.createIndividualPathSegments(stationsInfo, pathOpacity);
    }

    // A debounced rebuild can finish while the path toggle is off. New
    // segment meshes would otherwise return visible and pickable.
    if (!this.deps.isNavigationPathVisible()) {
      this.applyVisibility(false);
    }
  }

  private createIndividualPathSegments(
    stationsInfo: StationInfo[],
    pathOpacity: number
  ): void {
    const { segments } = createPathSegmentsVisuals(
      stationsInfo,
      (position) => this.deps.getStationZ(position),
      pathOpacity,
      this.visualConfig,
      MAP_STATION_PATH_COLOR
    );

    if (segments.length > 0) {
      this.pathSegments.push(...segments);

      const segmentMeshes = segments.map((s) => s.mesh);
      this.threeJsManager.addObject(...segmentMeshes);
    }
  }

  private createInstancedPaths(
    stationsInfo: StationInfo[],
    pathOpacity: number
  ): void {
    const farPathWidth =
      this.config.stationPlaneSize *
      this.config.pathWidthRatio *
      FAR_PATH_WIDTH_MULTIPLIER;

    const farResult = createInstancedPathMesh({
      stationsInfo,
      pathWidth: farPathWidth,
      pathOpacity,
      pathHeightOffset: PATH_HEIGHT_OFFSET,
      renderOrder: PATH_RENDER_ORDER,
      getStationZ: (position) => this.deps.getStationZ(position),
    });

    if (!farResult) {
      return;
    }

    this.farPathMesh = farResult.mesh;
    this.farPathData = farResult.instanceData;

    this.pathClipApplier.reset(farResult.instanceData.length);

    const instanceCount = farResult.instanceData.length;
    this.farPathTargetColors = new Array(instanceCount);
    this.farPathCurrentColors = new Array(instanceCount);

    this.nearPathMeshes.clear();
    this.nearPathMeshIdToSegmentIndex.clear();

    // All segments start as far (white); near meshes appear on LOD entry.
    for (let i = 0; i < instanceCount; i++) {
      this.farPathTargetColors[i] = LOD_FAR_PATH_COLOR.clone();
      this.farPathCurrentColors[i] = LOD_FAR_PATH_COLOR.clone();
      updateInstanceColor(this.farPathMesh, i, LOD_FAR_PATH_COLOR);
    }

    if (this.farPathMesh.instanceColor) {
      this.farPathMesh.instanceColor.needsUpdate = true;
    }
    this.farPathMesh.instanceMatrix.needsUpdate = true;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- InstancedMesh is compatible with Mesh for addObject
    this.threeJsManager.addObject(this.farPathMesh as any);
  }

  /** Direct position/rotation (vs instance matrix) keeps near segments jitter-free. */
  private createNearPathMesh(
    data: PathInstanceData,
    pathWidth: number,
    pathOpacity: number
  ): Mesh | null {
    const startData = this.deps.getStationDataByIndex(data.startStationIndex);
    const endData = this.deps.getStationDataByIndex(data.endStationIndex);

    if (!startData || !endData) {
      return null;
    }

    const startZ = this.deps.getStationZ(startData.originalPosition);
    const endZ = this.deps.getStationZ(endData.originalPosition);

    const start = new Vector3(
      startData.originalPosition.x,
      startData.originalPosition.y,
      startZ - PATH_HEIGHT_OFFSET
    );
    const end = new Vector3(
      endData.originalPosition.x,
      endData.originalPosition.y,
      endZ - PATH_HEIGHT_OFFSET
    );

    return createSinglePathSegmentMesh({
      start,
      end,
      pathWidth,
      pathOpacity,
      renderOrder: PATH_RENDER_ORDER,
      color: MAP_STATION_PATH_COLOR,
    });
  }

  /**
   * Applies LOD-driven colour immediately (no tween) and, for the instanced
   * path, performs the near↔far hybrid transition (creates/destroys
   * individual near meshes; zero-scales the matching instanced slot).
   */
  public updateTargetColors(markersVisible: boolean): void {
    if (this.config.useInstancedPaths) {
      this.updateInstancedPathTargetColors(markersVisible);
    } else {
      this.updateSegmentPathTargetColors(markersVisible);
    }
  }

  private updateSegmentPathTargetColors(markersVisible: boolean): void {
    for (const segment of this.pathSegments) {
      const startData = this.deps.getStationDataByIndex(
        segment.startStationIndex
      );
      const endData = this.deps.getStationDataByIndex(segment.endStationIndex);

      if (!startData || !endData) {
        continue;
      }

      const bothStationsNear =
        startData.isWithinMarkerDistance && endData.isWithinMarkerDistance;

      const newColor =
        bothStationsNear && markersVisible
          ? MAP_STATION_PATH_COLOR
          : LOD_FAR_PATH_COLOR;

      if (!segment.targetColor.equals(newColor)) {
        segment.targetColor.copy(newColor);
        segment.currentColor.copy(newColor);
        if (segment.mesh.material instanceof MeshBasicMaterial) {
          segment.mesh.material.color.copy(newColor);
        }
      }
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 23 > 15 allowed; flagged for later refactor, not fixed here
  private updateInstancedPathTargetColors(markersVisible: boolean): void {
    if (this.farPathData.length === 0 || !this.farPathMesh) {
      return;
    }

    let instancedMatrixNeedsUpdate = false;

    for (let i = 0; i < this.farPathData.length; i++) {
      const data = this.farPathData[i];
      const startData = this.deps.getStationDataByIndex(data.startStationIndex);
      const endData = this.deps.getStationDataByIndex(data.endStationIndex);

      if (!startData || !endData) {
        continue;
      }

      const isNear =
        startData.isWithinMarkerDistance && endData.isWithinMarkerDistance;
      const wasNear = this.nearPathMeshes.has(i);

      if (isNear && !wasNear) {
        const nearMesh = this.createNearPathMesh(
          data,
          this.config.stationPlaneSize * this.config.pathWidthRatio,
          this.currentPathOpacity
        );

        if (nearMesh) {
          const pathVisible = this.deps.isNavigationPathVisible();
          nearMesh.visible = pathVisible;
          this.setPathMeshPickable(nearMesh, pathVisible);
          this.nearPathMeshes.set(i, nearMesh);
          this.nearPathMeshIdToSegmentIndex.set(nearMesh.id, i);
          this.threeJsManager.addObject(nearMesh);

          this.hideInstancedSegment(i);
          instancedMatrixNeedsUpdate = true;
        }
      } else if (!isNear && wasNear) {
        this.destroyNearPathMesh(i);

        this.showInstancedSegment(i, data);
        instancedMatrixNeedsUpdate = true;
      }

      const newColor =
        isNear && markersVisible ? MAP_STATION_PATH_COLOR : LOD_FAR_PATH_COLOR;
      const targetColor = this.farPathTargetColors[i];
      const currentColor = this.farPathCurrentColors[i];

      if (!targetColor.equals(newColor)) {
        targetColor.copy(newColor);
        currentColor.copy(newColor);
        updateInstanceColor(this.farPathMesh, i, newColor);
      }

      // Newly-created near meshes start teal; the target check above
      // misses them when the target was already set by an earlier toggle,
      // so always sync the material colour explicitly here.
      const nearMesh = this.nearPathMeshes.get(i);
      if (
        nearMesh &&
        nearMesh.material instanceof MeshBasicMaterial &&
        !nearMesh.material.color.equals(newColor)
      ) {
        nearMesh.material.color.copy(newColor);
      }
    }

    if (instancedMatrixNeedsUpdate) {
      this.farPathMesh.instanceMatrix.needsUpdate = true;
    }

    if (this.farPathMesh.instanceColor) {
      this.farPathMesh.instanceColor.needsUpdate = true;
    }
  }

  private destroyNearPathMesh(segmentIndex: number): void {
    const mesh = this.nearPathMeshes.get(segmentIndex);
    if (!mesh) return;

    this.threeJsManager.removeObject(mesh);
    mesh.geometry.dispose();
    if (mesh.material instanceof MeshBasicMaterial) {
      mesh.material.dispose();
    }

    this.nearPathMeshIdToSegmentIndex.delete(mesh.id);
    this.nearPathMeshes.delete(segmentIndex);
  }

  /** Zero-scales the instance matrix (the cheapest way to hide it). */
  public hideInstancedSegment(instanceIndex: number): void {
    if (!this.farPathMesh) return;
    this.farPathMesh.getMatrixAt(instanceIndex, this.tempMatrix);
    this.tempMatrix.scale(StationPathPipeline.ZERO_SCALE);
    this.farPathMesh.setMatrixAt(instanceIndex, this.tempMatrix);
  }

  private showInstancedSegment(
    instanceIndex: number,
    data: PathInstanceData
  ): void {
    if (!this.farPathMesh) return;

    const startData = this.deps.getStationDataByIndex(data.startStationIndex);
    const endData = this.deps.getStationDataByIndex(data.endStationIndex);
    if (!startData || !endData) return;

    const startZ = this.deps.getStationZ(startData.originalPosition);
    const endZ = this.deps.getStationZ(endData.originalPosition);

    const start = new Vector3(
      startData.originalPosition.x,
      startData.originalPosition.y,
      startZ - PATH_HEIGHT_OFFSET
    );
    const end = new Vector3(
      endData.originalPosition.x,
      endData.originalPosition.y,
      endZ - PATH_HEIGHT_OFFSET
    );

    const direction = new Vector3().subVectors(end, start);
    const length = direction.length();
    direction.normalize();

    const yaw = Math.atan2(direction.y, direction.x);
    const pitch = Math.asin(-direction.z);

    const quaternion = new Quaternion();
    quaternion.setFromEuler(new Euler(0, pitch, yaw, 'ZYX'));

    const pathWidth = this.calculatePathWidthForSegment(data);

    const scale = new Vector3(length, pathWidth, 1);
    const matrix = new Matrix4();
    matrix.compose(data.midpoint, quaternion, scale);
    this.farPathMesh.setMatrixAt(instanceIndex, matrix);
  }

  /**
   * Returns `basePathWidth * distanceScale`. The far-path width multiplier
   * is baked in at creation time, not here. No near-zone scaling — see
   * StationMarkerPipeline.updateScale.
   */
  public calculatePathWidthForSegment(data: PathInstanceData): number {
    const basePathWidth =
      this.config.stationPlaneSize * this.config.pathWidthRatio;

    const distanceScale = calculateDistanceBasedScale(
      this.viewer.camera.position,
      data.midpoint,
      this.config.stationPointSize,
      undefined,
      this.config.stationFarBoundary
    );

    return basePathWidth * distanceScale;
  }

  /** Near segments use individual meshes; only far segments scale here. */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 19 > 15 allowed; flagged for later refactor, not fixed here
  public updateInstancedPathScales(markersVisible: boolean): boolean {
    if (!this.farPathMesh || this.farPathData.length === 0) {
      return false;
    }

    const cameraPosition = this.viewer.camera.position;
    const basePathWidth =
      this.config.stationPlaneSize * this.config.pathWidthRatio;
    let anyUpdated = false;

    for (let i = 0; i < this.farPathData.length; i++) {
      // Clipped instances have a zero-scale placeholder matrix; the applier
      // restores the cached pre-clip matrix on un-clip.
      if (this.pathClipApplier.isInstanceClipped(i)) {
        continue;
      }

      const nearMesh = this.nearPathMeshes.get(i);
      if (nearMesh) {
        // When markers hide, scale near-mesh Y to match the far path's
        // dynamic width so the path stays visually continuous.
        if (!markersVisible) {
          const distanceScale = calculateDistanceBasedScale(
            cameraPosition,
            this.farPathData[i].midpoint,
            this.config.stationPointSize,
            undefined,
            this.config.stationFarBoundary
          );
          if (
            Math.abs(nearMesh.scale.y - distanceScale) >
            PATH_WIDTH_CHANGE_THRESHOLD
          ) {
            nearMesh.scale.y = distanceScale;
            anyUpdated = true;
          }
        } else if (nearMesh.scale.y !== 1) {
          nearMesh.scale.y = 1;
          anyUpdated = true;
        }
        continue;
      }

      const data = this.farPathData[i];

      const distanceScale = calculateDistanceBasedScale(
        cameraPosition,
        data.midpoint,
        this.config.stationPointSize,
        undefined,
        this.config.stationFarBoundary
      );

      const newWidth = basePathWidth * distanceScale;

      const currentWidth = data.currentWidth ?? basePathWidth;
      if (Math.abs(newWidth - currentWidth) < PATH_WIDTH_CHANGE_THRESHOLD) {
        continue;
      }

      this.farPathMesh.getMatrixAt(i, this.tempMatrix);

      const currentYScale = getMatrixColumnMagnitude(
        this.tempMatrix.elements,
        MatrixColumn.Y
      );

      if (currentYScale > SCALE_ZERO_THRESHOLD) {
        scaleMatrixColumn(
          this.tempMatrix.elements,
          MatrixColumn.Y,
          newWidth / currentYScale
        );

        this.farPathMesh.setMatrixAt(i, this.tempMatrix);
        data.currentWidth = newWidth;
        anyUpdated = true;
      }
    }

    if (anyUpdated) {
      this.farPathMesh.instanceMatrix.needsUpdate = true;
    }

    return anyUpdated;
  }

  /** Returns true when the segment moved enough to warrant a refresh. */
  public updateSegmentScale(segment: PathSegment): boolean {
    const startData = this.deps.getStationDataByIndex(
      segment.startStationIndex
    );
    const endData = this.deps.getStationDataByIndex(segment.endStationIndex);

    if (!startData || !endData) {
      return false;
    }

    const distance = this.viewer.camera.position.distanceTo(segment.midpoint);

    if (
      Math.abs(distance - segment.distance) > SCALE_ANIMATION_DISTANCE_THRESHOLD
    ) {
      const scale = calculateDistanceBasedScale(
        this.viewer.camera.position,
        segment.midpoint,
        this.config.stationPointSize,
        undefined,
        this.config.stationFarBoundary
      );

      segment.mesh.scale.y = scale;
      segment.mesh.updateMatrixWorld(true);
      segment.distance = distance;

      return true;
    }

    return false;
  }

  /** Skips already-on-target segments (each frame this hits most of them). */
  public animateColors(): boolean {
    if (this.config.useInstancedPaths) {
      return this.animateInstancedPathColors();
    }

    let anyAnimating = false;

    for (const segment of this.pathSegments) {
      if (!(segment.mesh.material instanceof MeshBasicMaterial)) {
        continue;
      }

      const currentColor = segment.currentColor;
      const targetColor = segment.targetColor;

      const rDiff = Math.abs(targetColor.r - currentColor.r);
      const gDiff = Math.abs(targetColor.g - currentColor.g);
      const bDiff = Math.abs(targetColor.b - currentColor.b);

      if (
        rDiff <= ANIMATION_TOLERANCE &&
        gDiff <= ANIMATION_TOLERANCE &&
        bDiff <= ANIMATION_TOLERANCE
      ) {
        continue;
      }

      anyAnimating = true;

      currentColor.r +=
        Math.sign(targetColor.r - currentColor.r) *
        Math.min(COLOR_ANIMATION_SPEED, rDiff);
      currentColor.g +=
        Math.sign(targetColor.g - currentColor.g) *
        Math.min(COLOR_ANIMATION_SPEED, gDiff);
      currentColor.b +=
        Math.sign(targetColor.b - currentColor.b) *
        Math.min(COLOR_ANIMATION_SPEED, bDiff);

      segment.mesh.material.color.copy(currentColor);
    }

    if (!anyAnimating) {
      this.pathColorsAnimating = false;
    }

    return anyAnimating;
  }

  private animateInstancedPathColors(): boolean {
    if (!this.farPathMesh || this.farPathTargetColors.length === 0) {
      this.pathColorsAnimating = false;
      return false;
    }

    let anyAnimating = false;

    for (let i = 0; i < this.farPathTargetColors.length; i++) {
      const currentColor = this.farPathCurrentColors[i];
      const targetColor = this.farPathTargetColors[i];

      const rDiff = Math.abs(targetColor.r - currentColor.r);
      const gDiff = Math.abs(targetColor.g - currentColor.g);
      const bDiff = Math.abs(targetColor.b - currentColor.b);

      if (
        rDiff <= ANIMATION_TOLERANCE &&
        gDiff <= ANIMATION_TOLERANCE &&
        bDiff <= ANIMATION_TOLERANCE
      ) {
        continue;
      }

      anyAnimating = true;

      currentColor.r +=
        Math.sign(targetColor.r - currentColor.r) *
        Math.min(COLOR_ANIMATION_SPEED, rDiff);
      currentColor.g +=
        Math.sign(targetColor.g - currentColor.g) *
        Math.min(COLOR_ANIMATION_SPEED, gDiff);
      currentColor.b +=
        Math.sign(targetColor.b - currentColor.b) *
        Math.min(COLOR_ANIMATION_SPEED, bDiff);

      updateInstanceColor(this.farPathMesh, i, currentColor);
    }

    if (anyAnimating && this.farPathMesh.instanceColor) {
      this.farPathMesh.instanceColor.needsUpdate = true;
    } else if (!anyAnimating) {
      this.pathColorsAnimating = false;
    }

    return anyAnimating;
  }

  /** Drives shared opacity into every path material in the active mode. */
  // eslint-disable-next-line sonarjs/cognitive-complexity -- SonarJS trial (NUMARK-1243): complexity 16 > 15 allowed; flagged for later refactor, not fixed here
  public animateOpacity(): boolean {
    const diff = Math.abs(this.currentPathOpacity - this.targetPathOpacity);

    if (diff <= ANIMATION_TOLERANCE) {
      this.pathOpacityAnimating = false;
      return false;
    }

    this.currentPathOpacity +=
      Math.sign(this.targetPathOpacity - this.currentPathOpacity) *
      Math.min(OPACITY_ANIMATION_SPEED, diff);

    const opacity = this.currentPathOpacity;

    if (this.config.useInstancedPaths) {
      if (
        this.farPathMesh &&
        this.farPathMesh.material instanceof MeshBasicMaterial
      ) {
        (this.farPathMesh.material as MeshBasicMaterial).opacity = opacity;
      }
      for (const mesh of this.nearPathMeshes.values()) {
        if (mesh.material instanceof MeshBasicMaterial) {
          mesh.material.opacity = opacity;
        }
      }
    } else {
      for (const segment of this.pathSegments) {
        if (segment.mesh.material instanceof MeshBasicMaterial) {
          segment.mesh.material.opacity = opacity;
        }
      }
    }

    return true;
  }

  /**
   * On show, re-applies the clip volume immediately so clipped segments
   * don't briefly render outside it before the next heavy tick.
   */
  public applyVisibility(visible: boolean): void {
    if (this.config.useInstancedPaths) {
      if (this.farPathMesh) {
        this.farPathMesh.visible = visible;
        this.setPathMeshPickable(this.farPathMesh, visible);
      }
      this.nearPathMeshes.forEach((mesh) => {
        mesh.visible = visible;
        this.setPathMeshPickable(mesh, visible);
      });
    } else {
      this.pathSegments.forEach((segment) => {
        segment.mesh.visible = visible;
        this.setPathMeshPickable(segment.mesh, visible);
      });
    }
    if (visible) {
      this.applyClipping();
    }
    this.threeJsManager.forceRefresh();
  }

  /**
   * Hidden path meshes stay in the scene and remain raycastable until this
   * replaces their raycast. The saved function is restored on show, matching
   * the marker pipeline's originalRaycast pattern.
   */
  private setPathMeshPickable(mesh: Mesh, pickable: boolean): void {
    if (pickable) {
      const originalRaycast = this.pathMeshRaycasts.get(mesh);
      if (originalRaycast) {
        mesh.raycast = originalRaycast;
        this.pathMeshRaycasts.delete(mesh);
      }
      return;
    }

    if (!this.pathMeshRaycasts.has(mesh)) {
      this.pathMeshRaycasts.set(mesh, mesh.raycast);
    }
    mesh.raycast = ignorePathRaycast;
  }

  /**
   * Hide-if-either-endpoint-clipped, applied across all three rendering
   * modes. Marker visibility is gated separately by the marker pipeline's
   * LOD update.
   */
  public applyClipping(): boolean {
    if (!this.config.useInstancedPaths) {
      return this.pathClipApplier.applyToSegments(this.pathSegments);
    }

    let changed = this.pathClipApplier.applyToNearMeshes(
      this.nearPathMeshes,
      this.farPathData
    );
    if (this.farPathMesh && this.farPathData.length > 0) {
      changed =
        this.pathClipApplier.applyToInstancedFarPath(
          this.farPathMesh,
          this.farPathData,
          this.nearPathMeshes
        ) || changed;
    }

    return changed;
  }

  /**
   * `bothStationsNear` lets the manager skip path-mediated hover when the
   * endpoints already render as direct markers.
   */
  public resolveInstancedPathInstance(
    instanceId: number
  ): { data: PathInstanceData; bothStationsNear: boolean } | null {
    const data = this.farPathData[instanceId];
    if (!data) {
      console.warn(
        `[StationsNav] Invalid instanceId ${instanceId} for instanced path`
      );
      return null;
    }

    const startData = this.deps.getStationDataByIndex(data.startStationIndex);
    const endData = this.deps.getStationDataByIndex(data.endStationIndex);

    const bothStationsNear =
      (startData?.isWithinMarkerDistance ?? false) &&
      (endData?.isWithinMarkerDistance ?? false);

    return { data, bothStationsNear };
  }

  public resolveNearPathMeshId(meshId: number): PathInstanceData | null {
    const segmentIndex = this.nearPathMeshIdToSegmentIndex.get(meshId);
    if (segmentIndex === undefined) {
      return null;
    }
    return this.farPathData[segmentIndex] ?? null;
  }

  public findSegmentByMeshId(meshId: number): PathSegment | undefined {
    return this.pathSegments.find((s) => s.mesh.id === meshId);
  }

  public setTargetOpacity(opacity: number): void {
    if (this.targetPathOpacity !== opacity) {
      this.targetPathOpacity = opacity;
      this.pathOpacityAnimating = true;
    }
  }

  /** State is captured pre-await so a concurrent rebuild doesn't race. */
  public async disposeAll(): Promise<void> {
    const segmentsToRemove = this.pathSegments;
    const farPathMeshToRemove = this.farPathMesh;
    const nearPathMeshesToRemove = Array.from(this.nearPathMeshes.values());

    this.pathSegments = [];
    this.farPathMesh = null;
    this.farPathData = [];
    this.farPathTargetColors = [];
    this.farPathCurrentColors = [];
    this.pathClipApplier.reset(0);
    this.nearPathMeshes.clear();
    this.nearPathMeshIdToSegmentIndex.clear();

    await this.disposePathResources(
      segmentsToRemove,
      farPathMeshToRemove,
      nearPathMeshesToRemove
    );
  }

  /** Order mirrors the pre-extraction implementation exactly. */
  private async disposePathResources(
    segments: PathSegment[],
    farPathMesh: InstancedMesh | null,
    nearPathMeshes: Mesh[]
  ): Promise<void> {
    if (farPathMesh) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- InstancedMesh is compatible with Mesh for removeObject
      await this.threeJsManager.removeObject(farPathMesh as any);
      farPathMesh.geometry.dispose();
      if (farPathMesh.material) {
        if (Array.isArray(farPathMesh.material)) {
          farPathMesh.material.forEach((m) => m.dispose());
        } else {
          farPathMesh.material.dispose();
        }
      }
    }

    if (nearPathMeshes.length > 0) {
      await this.threeJsManager.removeObject(...nearPathMeshes);
      nearPathMeshes.forEach((mesh) => {
        mesh.geometry.dispose();
        if (mesh.material instanceof MeshBasicMaterial) {
          mesh.material.dispose();
        }
      });
    }

    if (segments.length > 0) {
      const meshes = segments.map((s) => s.mesh);
      await this.threeJsManager.removeObject(...meshes);
      segments.forEach((segment) => {
        segment.mesh.geometry.dispose();
        if (segment.mesh.material instanceof MeshBasicMaterial) {
          segment.mesh.material.dispose();
        }
      });
    }
  }
}
